/**
 * Markdown preview support for DiffViewer. Pure parsing utilities live in
 * `DiffViewer.parsing.ts`.
 */

import { useMemo } from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ParsedDiff } from './DiffViewer.parsing';

/**
 * Generate a URL-friendly slug from heading text.
 * Matches the convention used by GitHub / GitLab TOC anchor links:
 * lowercase, collapse whitespace to hyphens, strip non-alphanumeric (except hyphens).
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Custom heading renderers that inject id attributes so TOC anchor links work. */
const markdownHeadingComponents: Partial<Components> = {
  h1: ({ children, ...props }) => {
    const id = slugify(String(children));
    return (
      <h1 id={id} {...props}>
        {children}
      </h1>
    );
  },
  h2: ({ children, ...props }) => {
    const id = slugify(String(children));
    return (
      <h2 id={id} {...props}>
        {children}
      </h2>
    );
  },
  h3: ({ children, ...props }) => {
    const id = slugify(String(children));
    return (
      <h3 id={id} {...props}>
        {children}
      </h3>
    );
  },
  h4: ({ children, ...props }) => {
    const id = slugify(String(children));
    return (
      <h4 id={id} {...props}>
        {children}
      </h4>
    );
  },
  h5: ({ children, ...props }) => {
    const id = slugify(String(children));
    return (
      <h5 id={id} {...props}>
        {children}
      </h5>
    );
  },
  h6: ({ children, ...props }) => {
    const id = slugify(String(children));
    return (
      <h6 id={id} {...props}>
        {children}
      </h6>
    );
  },
};

/** Rendered markdown preview — shows the resulting file content (additions + context). */
export function MarkdownDiffPreview({ file }: { file: ParsedDiff }) {
  const afterContent = useMemo(() => {
    const lines: string[] = [];
    for (const chunk of file.chunks) {
      for (const line of chunk.lines) {
        if (line.type === 'add' || line.type === 'context') {
          lines.push(line.content);
        }
      }
    }
    return lines.join('\n');
  }, [file]);

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 p-6 prose prose-sm dark:prose-invert max-w-none overflow-auto">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownHeadingComponents}>
        {afterContent}
      </ReactMarkdown>
    </div>
  );
}
